// ---------------------------------------------------------------------------
// useEvalStats — fetch historical statistics for a set of evaluation metrics
// from WorkflowSvc. When the metric-name list is empty the hook stays idle and
// returns an empty map so the debug view can fall back to heuristics without a
// network call. Built on the shared `useAsyncData` primitive for race-safe
// loading + refresh.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { getEvalStats } from '../../services/evalStatsService.js';
import type { EvalStats } from '../../types/evalStats.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type {
  UseEvalStatsParams,
  UseEvalStatsResult,
} from './useEvalStats.interface.js';

const EMPTY_STATS = new Map<string, EvalStats>();

export function useEvalStats(params: UseEvalStatsParams): UseEvalStatsResult {
  const { agentFriendlyName, evalNames } = params;
  const client = useHttpClient();

  // Stable identity keyed on the sorted, de-duplicated set of names so the
  // fetcher (and therefore the request) only changes when the names change.
  const sortedKey = useMemo(
    () => Array.from(new Set(evalNames)).sort().join('\u0000'),
    [evalNames],
  );

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<Map<string, EvalStats>>) | null
  >(() => {
    if (agentFriendlyName.length === 0 || sortedKey.length === 0) return null;
    const names = sortedKey.split('\u0000');
    return () => getEvalStats(client, agentFriendlyName, names);
  }, [client, agentFriendlyName, sortedKey]);

  const { data, isLoading, error, refresh } = useAsyncData<
    Map<string, EvalStats>
  >(fetcher, { errorMessage: 'Failed to load metric statistics' });

  return useMemo<UseEvalStatsResult>(
    () => ({ stats: data ?? EMPTY_STATS, isLoading, error, refresh }),
    [data, isLoading, error, refresh],
  );
}
